import React from "react"
import { css, type Interpolation, type Theme } from "@emotion/react"
import { Medium20, Regular16Paragraph } from "@myvp/shared/src/typography"
import Button from "@myvp/shared/src/components/buttons/button"
import BaseDialog from "@myvp/shared/src/components/base-dialog"
import Warning from "@myvp/shared/src/icons/warning"
import Alert from "@myvp/shared/src/icons/alert"
import Banner from "@myvp/shared/src/components/banner"
import Success from "@myvp/shared/src/icons/success"

export interface MessageDialogProps {
  className?: string
  variant?: "warning" | "alert" | "message" | "success"
  dialogCss?: Interpolation<Theme>
  confirmButtonVariant?: React.ComponentProps<typeof Button>["variant"]
  title: React.ReactNode
  message: React.ReactNode
  onClose?: () => void
  disabledConfirm?: boolean
  onConfirm?: () => void
  onConfirmText?: React.ReactNode
  onCloseText: React.ReactNode | false
  disabledClose?: boolean
  visible?: boolean
  closeButtonAutomationId?: string
  confirmButtonAutomationId?: string
  fields?: React.ReactNode
  messageAs?: Regular16ParagraphProps["as"]
  titleAs?: Medium20Props["as"]
  /**
   * It's intentional that banner only accepts message and it only displays "error" variant right now to keep it scoped
   * to just this use case
   */
  banner?: {
    message: React.ReactNode
  }
}

type Regular16ParagraphProps = React.ComponentProps<typeof Regular16Paragraph>
type Medium20Props = React.ComponentProps<typeof Medium20>

const MessageDialog = (props: MessageDialogProps) => {
  const titleId = React.useId()
  return (
    <BaseDialog
      className={props.className}
      aria-labelledby={titleId}
      visible={props.visible ?? true}
      onCancel={(event) => {
        if (!props.onClose) {
          // prevent closing when escape key is pressed
          event.preventDefault()
        }
      }}
      onClose={props.onClose}
      dialogCss={[
        css`
          word-break: break-word;
          width: min(496px, calc(100dvw - 48px));
          min-height: 176px;
          height: auto;
          max-height: calc(100dvh - 48px);
          padding: 24px;
        `,
        props.dialogCss,
      ]}
    >
      <MessageDialogBody {...props} titleId={titleId} />
    </BaseDialog>
  )
}

export default MessageDialog

const getIcon = (
  variant: React.ComponentProps<typeof MessageDialog>["variant"]
) => {
  switch (variant) {
    case "warning":
      return <Warning variant="filled" size="medium" />
    case "alert":
      return <Alert variant="filled" size="medium" />
    case "success":
      return <Success variant="filled" size="medium" />
    case "message":
    default:
      return null
  }
}

export function MessageDialogBody(
  props: MessageDialogProps & {
    titleId?: string
  }
) {
  return (
    <>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
        `}
      >
        {getIcon(props.variant)}
        <Medium20
          {...(props.titleAs ? { as: props.titleAs } : {})}
          id={props.titleId}
        >
          {props.title}
        </Medium20>
      </div>
      {typeof props.message === "string" ? (
        <Regular16Paragraph
          {...(props.messageAs ? { as: props.messageAs } : {})}
          css={css`
            margin-block: 16px;
            text-align: start;
            text-overflow: ellipsis;
            overflow: hidden;
            overflow-wrap: anywhere;
          `}
        >
          {props.message}
        </Regular16Paragraph>
      ) : (
        props.message
      )}
      {props.banner ? (
        <Banner
          css={css`
            margin-bottom: 24px;
          `}
          message={props.banner.message}
          variant="error"
        />
      ) : null}
      {props.fields}
      <div
        css={css`
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: minmax(0px, 1fr);
          column-gap: 16px;
        `}
      >
        {props.onCloseText !== false && !!props.onClose && (
          <Button
            variant="secondary"
            height="large"
            onClick={props.onClose}
            disabled={props.disabledClose}
            data-automationid={props.closeButtonAutomationId}
          >
            {props.onCloseText}
          </Button>
        )}
        {Boolean(props.onConfirm && props.onConfirmText) && (
          <Button
            variant={props.confirmButtonVariant ?? "primary"}
            height="large"
            disabled={props.disabledConfirm}
            onClick={props.onConfirm}
            data-automationid={props.confirmButtonAutomationId}
          >
            {props.onConfirmText}
          </Button>
        )}
      </div>
    </>
  )
}
